/**
 * ParcelSummarySheet — phone-only bottom sheet triggered by tapping a parcel
 * marker on the map (#704 PR-3).
 *
 * On tablet/desktop the existing CircleMarker Popup is the canonical surface;
 * on phone the Popup's content overflows a 390-pt viewport and the user has
 * to dismiss it to interact with the map. SearchPage replaces the Popup with
 * this bottom sheet on the phone breakpoint: tap-marker → state-set → sheet
 * slides up with a compact summary card. "View Detail →" is a real Link to
 * the existing /parcel/:id route, so the deep-detail flow is unchanged once
 * the user decides to drill in.
 *
 * Shares the slide-up + backdrop pattern with MoreSheet and FilterSheet so
 * all three phone sheets feel like siblings; the lighter padding + smaller
 * max-height differentiates a "preview" sheet from a "navigation" sheet.
 *
 * Props
 * -----
 *   parcel             : MapView point object (parcel_id, listing_address,
 *                        listing_price, area_acres, opportunity_score,
 *                        county, state, ...); null when no parcel selected.
 *   onClose            : called when backdrop tapped / Escape pressed.
 *   isBookmarked       : current bookmark state (boolean) so the Save button
 *                        can render as "Save" vs "Saved".
 *   onToggleBookmark   : called with the parcel_id on Save-tap. Parent owns
 *                        the create/delete API call so this component stays
 *                        presentational.
 *   isSaving           : optional — disables the Save button during a
 *                        pending API call.
 *
 * Reused PR-4 note: the blueprint flags this layout as the foundation for
 * PR-4's compact ParcelDetail header. Keep card layout simple (price /
 * acres / score / address) so PR-4 can lift the same component to a
 * non-modal context with minimal change.
 */
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { parcelPath } from '../utils/parcelPath';

// Local price formatter — mirrors MapView's so phone-summary numbers
// look identical to popup numbers (no surprise reformatting between
// the two surfaces).
function formatPrice(n) {
  const num = Number(n);
  if (!Number.isFinite(num) || num <= 0) return null;
  if (num >= 1_000_000) return `$${(num / 1_000_000).toFixed(num >= 10_000_000 ? 1 : 2)}M`;
  if (num >= 1_000) return `$${Math.round(num / 1_000).toLocaleString()}K`;
  return `$${Math.round(num).toLocaleString()}`;
}

function formatAcres(n) {
  const num = Number(n);
  if (!Number.isFinite(num) || num <= 0) return null;
  if (num >= 1000) return `${Math.round(num).toLocaleString()} ac`;
  return `${num.toLocaleString(undefined, { maximumFractionDigits: 1 })} ac`;
}

function formatScore(n) {
  const num = Number(n);
  if (!Number.isFinite(num)) return null;
  // Score in DB is 0..1 or 0..100 depending on view; treat anything ≤1 as
  // a fraction and scale, otherwise show as-is. Matches PropertyCard's
  // rule so the same number reads consistently across surfaces.
  const scaled = num <= 1 ? Math.round(num * 100) : Math.round(num);
  return String(scaled);
}

function ParcelSummarySheet({
  parcel,
  onClose,
  isBookmarked = false,
  onToggleBookmark,
  isSaving = false,
}) {
  const open = !!parcel;

  // Escape-to-dismiss + body-scroll lock while open. Mirrors MoreSheet
  // and FilterSheet for consistency.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  // Address fallback chain matches MapView's popup: real address →
  // county+state → parcel_id. Never blank.
  const headerTitle = parcel
    ? (parcel.listing_address
        || (parcel.county && parcel.state ? `${parcel.county}, ${parcel.state}` : null)
        || parcel.parcel_id)
    : '';

  const price = parcel ? formatPrice(parcel.listing_price) : null;
  const acres = parcel ? formatAcres(parcel.area_acres) : null;
  const score = parcel ? formatScore(parcel.opportunity_score) : null;
  const subtitle = parcel
    ? [parcel.county, parcel.state].filter(Boolean).join(', ')
    : '';

  const handleSaveClick = () => {
    if (!parcel || isSaving) return;
    if (typeof onToggleBookmark === 'function') {
      onToggleBookmark(parcel.parcel_id);
    }
  };

  return (
    <div
      className={`parcel-summary-sheet-root ${open ? 'parcel-summary-sheet-root--open' : ''}`}
      data-testid="parcel-summary-sheet-root"
      aria-hidden={!open}
    >
      <div
        className="parcel-summary-sheet-backdrop"
        onClick={onClose}
        role="presentation"
        data-testid="parcel-summary-sheet-backdrop"
      />

      <div
        className="parcel-summary-sheet"
        role="dialog"
        aria-modal="true"
        aria-label="Parcel summary"
        data-testid="parcel-summary-sheet"
      >
        <div className="parcel-summary-sheet-handle" aria-hidden="true" />

        {parcel && (
          <div className="parcel-summary-card">
            {/* Header row: title + listing badge */}
            <div className="parcel-summary-header">
              <div className="parcel-summary-title-block">
                <div
                  className="parcel-summary-title"
                  data-testid="parcel-summary-title"
                >
                  {headerTitle}
                </div>
                {subtitle && (
                  <div className="parcel-summary-subtitle">
                    {subtitle}
                  </div>
                )}
              </div>
              {parcel.is_listed && (
                <span
                  className="parcel-summary-listed-badge"
                  data-testid="parcel-summary-listed-badge"
                >
                  LISTED
                </span>
              )}
            </div>

            {/* Vital stats row */}
            <div className="parcel-summary-stats">
              {price && (
                <div
                  className="parcel-summary-stat parcel-summary-stat--price"
                  data-testid="parcel-summary-price"
                >
                  {price}
                </div>
              )}
              {acres && (
                <div
                  className="parcel-summary-stat"
                  data-testid="parcel-summary-acres"
                >
                  {acres}
                </div>
              )}
              {score != null && (
                <div
                  className="parcel-summary-stat"
                  data-testid="parcel-summary-score"
                >
                  Score {score}
                </div>
              )}
            </div>

            {/* Action row: Save toggle + View Detail link */}
            <div className="parcel-summary-actions">
              <button
                type="button"
                className={`parcel-summary-save ${isBookmarked ? 'parcel-summary-save--active' : ''}`}
                onClick={handleSaveClick}
                disabled={isSaving}
                aria-pressed={isBookmarked}
                aria-label={isBookmarked ? 'Remove bookmark' : 'Save parcel'}
                data-testid="parcel-summary-save"
              >
                {isBookmarked ? 'Saved' : 'Save'}
              </button>
              <Link
                to={parcelPath(parcel.parcel_id, { state: parcel.state, county: parcel.county })}
                className="parcel-summary-detail-link"
                onClick={onClose}
                data-testid="parcel-summary-detail-link"
              >
                View Detail →
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ParcelSummarySheet;
